import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseModal } from '@/components/modal/BaseModal';
import { Flag } from 'lucide-react';
import { http } from '@/network/http';
import toast from 'react-hot-toast';

interface ReportContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: 'post' | 'message';
  contentId: string;
}

const REASONS = ['spam', 'harassment', 'inappropriate', 'other'] as const;

export function ReportContentModal({ isOpen, onClose, contentType, contentId }: ReportContentModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<string>('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason) return;
    try {
      setLoading(true);
      const endpoint = contentType === 'post'
        ? `/feed/${contentId}/report`
        : `/messaging/messages/${contentId}/report`;
      await http.post(endpoint, { reason, description: description || undefined });
      toast.success(t('feed.reportSubmitted'));
      onClose();
      setReason('');
      setDescription('');
    } catch (err: any) {
      if (err?.response?.status === 409) {
        toast.error(t('feed.alreadyReported'));
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} mobileVariant="bottomsheet" ariaLabel={t('feed.reportContent')}>
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center gap-2">
          <Flag className="h-5 w-5 text-red-500" />
          <h2 className="text-lg font-bold" style={{ color: 'var(--user-page-heading)' }}>{t('feed.reportContent')}</h2>
        </div>
        <p className="mt-1 text-sm" style={{ color: 'var(--user-page-text)' }}>{t('feed.reportDescription')}</p>
      </div>

      <div className="px-5 space-y-2">
        {REASONS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setReason(r)}
            className={`w-full rounded-xl px-4 py-3 text-sm font-medium text-start transition-colors cursor-pointer ${
              reason === r
                ? 'bg-red-50 text-red-700 ring-1 ring-red-200'
                : 'hover-card'
            }`}
            style={reason !== r ? { backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-heading)' } : undefined}
          >
            {t(`feed.reportReason_${r}`)}
          </button>
        ))}
      </div>

      {reason === 'other' && (
        <div className="px-5 mt-3">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('feed.reportDetailsPlaceholder')}
            rows={3}
            className="w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-red-200"
            style={{ backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-heading)' }}
          />
        </div>
      )}

      <div className="px-5 py-4">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!reason || loading}
          className="w-full rounded-xl bg-red-500 py-3 text-sm font-bold text-white transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {loading ? '...' : t('feed.submitReport')}
        </button>
      </div>
    </BaseModal>
  );
}
